import { getUnreadCount, getNotifications, markAsRead, markAllAsRead } from '@/api/notification'

class NotificationService {
  constructor() {
    this.subscribers = []
    this.notificationPollingInterval = null
    this.unreadCount = 0
    this.notifications = []
    this.lastUpdateTime = null
    this.isPolling = false
  }

  /**
   * 添加订阅者
   * @param {Function} callback 回调函数
   * @returns {Function} 取消订阅的函数
   */
  subscribe(callback) {
    this.subscribers.push(callback)
    
    // 立即通知新订阅者当前状态
    if (this.lastUpdateTime) {
      callback({
        unreadCount: this.unreadCount,
        notifications: this.notifications,
        lastUpdateTime: this.lastUpdateTime
      })
    }
    
    // 返回取消订阅的函数
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback)
      
      // 如果没有订阅者了，停止轮询
      if (this.subscribers.length === 0) {
        this.stopPolling()
      }
    }
  }

  /**
   * 通知所有订阅者
   */
  notifySubscribers() {
    const data = {
      unreadCount: this.unreadCount,
      notifications: this.notifications,
      lastUpdateTime: this.lastUpdateTime
    }
    
    this.subscribers.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error('通知订阅者时出错:', error)
      }
    })
  }

  /**
   * 开始轮询通知
   * @param {Number} interval 轮询间隔，单位毫秒，默认60秒
   */
  startPolling(interval = 60000) {
    if (this.isPolling) {
      return
    }
    
    this.isPolling = true
    
    // 立即获取一次通知
    this.fetchUnreadCount()
    this.fetchNotifications()
    
    // 设置定时器
    this.notificationPollingInterval = setInterval(() => {
      this.fetchUnreadCount()
      
      // 只有当有未读消息时才获取通知列表，减少不必要的请求
      if (this.unreadCount > 0) {
        this.fetchNotifications()
      }
    }, interval)
  }

  /**
   * 停止轮询
   */
  stopPolling() {
    if (this.notificationPollingInterval) {
      clearInterval(this.notificationPollingInterval)
      this.notificationPollingInterval = null
      this.isPolling = false
    }
  }

  /**
   * 获取未读通知数量
   */
  async fetchUnreadCount() {
    try {
      const res = await getUnreadCount()
      if (res.code === 200) {
        const oldCount = this.unreadCount
        this.unreadCount = res.data
        
        // 如果未读数量变化，更新最后更新时间并通知订阅者
        if (oldCount !== this.unreadCount) {
          this.lastUpdateTime = new Date()
          this.notifySubscribers()
        }
      }
    } catch (error) {
      console.error('获取未读通知数量失败:', error)
    }
  }

  /**
   * 获取通知列表
   * @param {Object} params 查询参数
   */
  async fetchNotifications(params = { page: 1, pageSize: 10 }) {
    try {
      const res = await getNotifications(params)
      if (res.code === 200) {
        this.notifications = res.data.list
        this.lastUpdateTime = new Date()
        this.notifySubscribers()
      }
    } catch (error) {
      console.error('获取通知列表失败:', error)
    }
  }

  /**
   * 标记通知为已读
   * @param {String|Array} ids 通知ID或ID数组
   */
  async markAsRead(ids) {
    try {
      const res = await markAsRead(ids)
      if (res.code === 200) {
        // 更新本地数据
        if (Array.isArray(ids)) {
          this.notifications = this.notifications.map(notification => {
            if (ids.includes(notification.id)) {
              return { ...notification, isRead: true }
            }
            return notification
          })
        } else {
          this.notifications = this.notifications.map(notification => {
            if (notification.id === ids) {
              return { ...notification, isRead: true }
            }
            return notification
          })
        }
        
        // 重新获取未读数量
        await this.fetchUnreadCount()
        
        // 更新最后更新时间并通知订阅者
        this.lastUpdateTime = new Date()
        this.notifySubscribers()
      }
    } catch (error) {
      console.error('标记通知已读失败:', error)
      throw error
    }
  }

  /**
   * 标记所有通知为已读
   */
  async markAllAsRead() {
    try {
      const res = await markAllAsRead()
      if (res.code === 200) {
        // 更新本地数据
        this.notifications = this.notifications.map(notification => ({
          ...notification,
          isRead: true
        }))
        
        // 未读数量置为0
        this.unreadCount = 0
        
        // 更新最后更新时间并通知订阅者
        this.lastUpdateTime = new Date()
        this.notifySubscribers()
      }
    } catch (error) {
      console.error('标记所有通知已读失败:', error)
      throw error
    }
  }
}

// 创建单例实例
const notificationService = new NotificationService()

export default notificationService 